import { type App, inject } from 'vue'
import { defer, type Deferred } from '../defer.ts'

export function isRedirect(
    redirect: RedirectLocation
): redirect is Required<RedirectLocation> {
    return Boolean(
        redirect.status && redirect.status >= 300 && redirect.status < 400
    )
}

export type ServerRedirectState = {
    deferred: Deferred<string>
    redirectLocation: RedirectLocation
    isRedirect: () => boolean
}

export type RedirectLocation = {
    url?: string
    status?: number
}

export type RedirectFunction = (
    location: string,
    status?: number
) => Promise<void>

export function createServerRedirectState(): ServerRedirectState {
    const deferred = defer<string>()
    const redirectLocation: RedirectLocation = {} as RedirectLocation

    return {
        deferred,
        redirectLocation,
        isRedirect: () => isRedirect(redirectLocation),
    }
}

export function createServerRedirect(
    state: ServerRedirectState
): RedirectFunction {
    const { redirectLocation, deferred } = state

    return async (location: string, status = 302): Promise<void> => {
        const newLocation: RedirectLocation = {
            url: location,
            status: status,
        }
        Object.assign(redirectLocation, newLocation)
        if (isRedirect(redirectLocation)) {
            // Stop waiting for rendering when redirecting
            deferred.resolve('')
        }
    }
}

export function createClientRedirect(
    spaRedirect: (location: string, status?: number) => Promise<void>
): RedirectFunction {
    return async (location: string, status?: number): Promise<void> => {
        if (location.startsWith('/')) {
            await spaRedirect(location, status)
        } else {
            // eslint-disable-next-line no-restricted-globals
            window.location.href = location
        }
    }
}

export const REDIRECT_SYMBOL_WANNABE = `app.plugin.redirect`

export function provideRedirect(app: App, redirect: RedirectFunction): void {
    app.provide(REDIRECT_SYMBOL_WANNABE, redirect)
}

export function useRedirect(): RedirectFunction {
    return inject(REDIRECT_SYMBOL_WANNABE) as RedirectFunction
}
